import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Text } from '@stashinvest/react-components';
import { StarBoldIcon, XIcon } from '@stashinvest/react-assets';
import {
	dismissContentCard,
	logContentCardClick,
	logContentCardImpression,
} from './contentCardsService';
import {
	formatCardTime,
	formatSpotlightDay,
	isInternalAppPath,
	isSafeExternalUrl,
	toInternalPath,
} from './bucketing';
import type { NotificationCenterItem } from './types';

type NotificationItemCardProps = {
	item: NotificationCenterItem;
};

type CardImageProps = {
	url: string;
	layout: 'thumbnail' | 'hero';
};

function CardImage({ url, layout }: CardImageProps) {
	const [hasError, setHasError] = useState(false);

	if (hasError) {
		return null;
	}

	if (layout === 'thumbnail') {
		// App.css sets `img { width: 100% }` after Tailwind utilities, so size via
		// inline styles + a fixed wrapper (classic = small side image).
		return (
			<div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg">
				<img
					src={url}
					alt=""
					referrerPolicy="no-referrer"
					onError={() => setHasError(true)}
					style={{ width: 48, height: 48, objectFit: 'contain' }}
				/>
			</div>
		);
	}

	return (
		<img
			src={url}
			alt=""
			referrerPolicy="no-referrer"
			onError={() => setHasError(true)}
			className="mt-content2XS max-h-[180px] w-full rounded-lg object-contain"
			style={{ width: '100%', maxHeight: 180, objectFit: 'contain' }}
		/>
	);
}

export function NotificationItemCard({ item }: NotificationItemCardProps) {
	const history = useHistory();
	const cardRef = useRef<HTMLElement>(null);
	const hasLoggedImpression = useRef(false);
	const isSpotlight = item.isPinned;
	const imageLayout = item.imageLayout ?? (item.imageUrl ? 'hero' : undefined);
	const showThumbnail = Boolean(item.imageUrl && imageLayout === 'thumbnail');
	const showHero = Boolean(item.imageUrl && imageLayout === 'hero');

	useEffect(() => {
		const node = cardRef.current;
		if (!node || hasLoggedImpression.current) {
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					hasLoggedImpression.current = true;
					void logContentCardImpression(item.id);
					observer.disconnect();
				}
			},
			{ threshold: 0.5 }
		);

		observer.observe(node);
		return () => observer.disconnect();
	}, [item.id]);

	const handleCtaClick = () => {
		void logContentCardClick(item.id);

		if (!item.ctaUrl) {
			return;
		}

		if (isInternalAppPath(item.ctaUrl)) {
			history.push(toInternalPath(item.ctaUrl));
			return;
		}

		if (isSafeExternalUrl(item.ctaUrl)) {
			window.open(item.ctaUrl, '_blank', 'noopener,noreferrer');
		}
	};

	const handleDismiss = () => {
		void dismissContentCard(item.id);
	};

	return (
		<article
			ref={cardRef}
			className={`relative w-full rounded-xl p-contentMedium text-left ${
				isSpotlight ? 'bg-primaryContainer' : 'bg-containerHighest'
			}`}
			aria-label={item.title}
		>
			{item.isDismissible && (
				<button
					type="button"
					aria-label="Dismiss notification"
					onClick={handleDismiss}
					className="absolute right-content2XS top-content2XS z-[1] flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent text-onSurfaceVariant hover:bg-scrim/opacityScrim"
				>
					<XIcon size="2XS" />
				</button>
			)}

			{isSpotlight && (
				<div className="mb-content2XS flex items-center gap-content3XS pr-contentLarge">
					<StarBoldIcon size="2XS" className="text-onPrimaryContainer" />
					<Text
						tag="span"
						textStyle="bodySmallEmphasize"
						className="text-onPrimaryContainer"
					>
						Spotlight
					</Text>
					<Text
						tag="span"
						textStyle="bodySmallStandard"
						className="ml-auto text-onPrimaryContainer"
					>
						{formatSpotlightDay(item.createdAt)}
					</Text>
				</div>
			)}

			<div
				className={`flex items-start gap-content2XS ${
					item.isDismissible ? 'pr-contentLarge' : ''
				}`}
			>
				{showThumbnail && item.imageUrl ? (
					<CardImage url={item.imageUrl} layout="thumbnail" />
				) : null}
				<div className="min-w-0 flex-1">
					<div className="flex items-start gap-content2XS">
						<Text
							tag="h3"
							textStyle="bodyMediumEmphasize"
							color={isSpotlight ? undefined : 'text-onSurface'}
							className={`line-clamp-2 min-w-0 flex-1 ${
								isSpotlight ? 'text-onPrimaryContainer' : ''
							}`}
						>
							{item.title}
						</Text>
						{!isSpotlight && (
							<Text
								tag="span"
								textStyle="bodySmallStandard"
								color="text-onSurfaceVariant"
								className="shrink-0"
							>
								{formatCardTime(item.createdAt)}
							</Text>
						)}
						{!item.isRead && (
							<span
								aria-label="Unread"
								className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary"
							/>
						)}
					</div>

					{item.body ? (
						<Text
							tag="p"
							textStyle="bodySmallStandard"
							color={isSpotlight ? undefined : 'text-onSurfaceVariant'}
							className={`mt-content2XS ${
								isSpotlight ? 'text-onPrimaryContainer' : ''
							}`}
						>
							{item.body}
						</Text>
					) : null}

					{showHero && item.imageUrl ? (
						<CardImage url={item.imageUrl} layout="hero" />
					) : null}

					{item.ctaText && item.ctaUrl ? (
						<button
							type="button"
							onClick={handleCtaClick}
							className="mt-content2XS border-0 bg-transparent p-0 text-left"
						>
							<Text tag="span" textStyle="bodySmallEmphasize" color="text-primary">
								{item.ctaText}
							</Text>
						</button>
					) : null}
				</div>
			</div>
		</article>
	);
}
